import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  CircularProgress,
  Fade,
  Avatar,
} from '@mui/material';
import {
  SportsTennis,
  Add,
  GroupAdd,
  People,
  ArrowForward,
  FiberManualRecord,
  EmojiEvents,
  Settings,
  Dashboard,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { leagueService } from '../services/firestore';
import AppHeader from '../components/common/AppHeader';
import type { League } from '../types/database';
import colors from '../theme/colors';
import { alpha } from '@mui/material/styles';

// ── Shared style tokens ─────────────────────────────────────────────────────

const glassCard = {
  background: `linear-gradient(145deg, ${alpha(colors.blue.navy, 0.95)} 0%, ${alpha('#0A1929', 0.98)} 100%)`,
  backdropFilter: 'blur(24px)',
  border: `1px solid ${colors.border.default}`,
  borderRadius: 4,
  boxShadow: `0 20px 60px rgba(0,0,0,0.4), 0 1px 0 ${alpha(colors.blue.electric, 0.15)} inset`,
  overflow: 'hidden',
  position: 'relative' as const,
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0, left: 0, right: 0,
    height: '1px',
    background: `linear-gradient(90deg, transparent, ${alpha(colors.blue.electric, 0.6)}, transparent)`,
  },
};

const getLeagueStatus = (league: League) => {
  const now = new Date();
  const ended = league.endDate ? now > new Date(league.endDate) : league.status === 'completed';
  const started = now > new Date(league.startDate);
  if (ended)   return { color: colors.grey[500],       label: 'Completed',  pulse: false };
  if (started) return { color: colors.success.primary, label: 'In Progress', pulse: true  };
  return       { color: colors.orange.primary,          label: 'Upcoming',   pulse: false };
};

// ── Component ────────────────────────────────────────────────────────────────

const TennisHubPage: React.FC = () => {
  const { user, userData } = useAuth();
  const navigate = useNavigate();
  const [tennisLeagues, setTennisLeagues] = useState<League[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const all = await leagueService.getAll();
        setTennisLeagues(
          all.filter(l =>
            l.sport === 'tennis' &&
            (l.participants.includes(user?.uid ?? '') || l.creatorId === user?.uid)
          )
        );
      } catch (e) {
        console.error('Error loading tennis leagues:', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user]);

  const slotMultipliers = [
    { slot: 'M1 / W1', mult: '2.00x', fill: 100 },
    { slot: 'M2 / W2', mult: '1.75x', fill: 87  },
    { slot: 'M3 / W3', mult: '1.50x', fill: 75  },
    { slot: 'M4 / W4', mult: '1.25x', fill: 62  },
    { slot: 'M5 / W5', mult: '1.00x', fill: 50  },
  ];

  const pointEvents = [
    { label: 'Match Win',                   pts: '+20 pts'     },
    { label: 'Straight Sets Bonus',         pts: '+10 pts'     },
    { label: 'Final Set Tie-Break Win',     pts: '+10 pts'     },
    { label: 'Dominant Set (6-0 or 6-1)',   pts: '+10 pts/set' },
    { label: 'Upset (5+ seed diff)',        pts: '+10 pts'     },
    { label: 'Major Upset (15+ seed diff)', pts: '+16 pts'     },
    { label: 'Correct Winner Prediction',  pts: '+10 pts'     },
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppHeader />

      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <Fade in timeout={700}>
        <Box sx={{
          minHeight: { xs: '34vh', md: '38vh' },
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          position: 'relative', overflow: 'hidden',
          background: colors.gradients.hero,
          '&::before': {
            content: '""', position: 'absolute',
            top: '-50%', left: '-50%', width: '200%', height: '200%',
            background: `radial-gradient(circle, ${alpha(colors.blue.electric, 0.13)} 0%, transparent 70%),
                         radial-gradient(circle at 80% 20%, ${alpha(colors.orange.primary, 0.09)} 0%, transparent 50%)`,
            animation: 'gradientShift 15s ease infinite', pointerEvents: 'none',
          },
          '@keyframes gradientShift': {
            '0%, 100%': { transform: 'translate(0,0) rotate(0deg)' },
            '50%':      { transform: 'translate(-5%,-5%) rotate(180deg)' },
          },
        }}>
          <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1, py: 5, textAlign: 'center' }}>

            {/* TENNIS edition badge */}
            <Box sx={{
              display: 'inline-flex', alignItems: 'center', gap: 0.75, mb: 2.5,
              px: 1.5, py: 0.45,
              border: `1px solid ${alpha(colors.blue.electric, 0.22)}`,
              borderRadius: '20px',
              bgcolor: alpha(colors.blue.electric, 0.07),
            }}>
              <SportsTennis sx={{ fontSize: 11, color: alpha(colors.blue.light, 0.8) }} />
              <Typography sx={{
                fontFamily: "'Montserrat', sans-serif", fontWeight: 700,
                fontSize: '0.62rem', letterSpacing: '0.28em',
                color: alpha(colors.blue.light, 0.8),
                textTransform: 'uppercase',
              }}>
                Tennis
              </Typography>
            </Box>

            {/* Main title */}
            <Typography sx={{
              fontFamily: "'Fraunces', serif", fontWeight: 800, mb: 2,
              fontSize: { xs: '2.1rem', md: '3rem', lg: '3.4rem' },
              background: colors.gradients.title,
              backgroundSize: '200% 200%', backgroundClip: 'text',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
              animation: 'gradientFlow 8s ease infinite',
              lineHeight: 1.1, fontStyle: 'normal',
              '@keyframes gradientFlow': {
                '0%, 100%': { backgroundPosition: '0% 50%' },
                '50%':      { backgroundPosition: '100% 50%' },
              },
            }}>
              Fantasy Community League
            </Typography>

            {/* Grand Slams line - tennis ball optic yellow-green */}
            <Typography sx={{
              fontFamily: "'Satoshi', sans-serif",
              fontSize: { xs: '0.62rem', md: '0.7rem' },
              letterSpacing: '0.12em',
              color: alpha('#C8F000', 0.65),
              textTransform: 'uppercase',
            }}>
              Australian Open&nbsp;&nbsp;·&nbsp;&nbsp;Roland Garros&nbsp;&nbsp;·&nbsp;&nbsp;Wimbledon&nbsp;&nbsp;·&nbsp;&nbsp;US Open
            </Typography>

          </Container>
        </Box>
      </Fade>

      {/* ── User Status Card ─────────────────────────────────────────────── */}
      <Container maxWidth="lg" sx={{ pt: 2, pb: 0, position: 'relative', zIndex: 1 }}>
        <Fade in timeout={900}>
          <Card sx={glassCard}>

            {/* Top row: avatar + greeting + actions */}
            <Box sx={{
              px: { xs: 3, md: 5 }, pt: { xs: 3, md: 4 }, pb: 3,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              flexWrap: 'wrap', gap: 3,
            }}>
              {/* Left: avatar + text */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
                <Box sx={{ position: 'relative' }}>
                  <Avatar
                    src={(userData as any)?.profilePicUrl}
                    sx={{
                      width: 72, height: 72,
                      background: `linear-gradient(135deg, ${colors.blue.deep} 0%, ${colors.blue.electric} 100%)`,
                      fontSize: '1.75rem', fontWeight: 700,
                      boxShadow: `0 0 0 3px ${alpha(colors.blue.electric, 0.25)}, ${colors.shadows.blue.md}`,
                    }}
                  >
                    {(userData as any)?.displayName?.charAt(0) || user?.email?.charAt(0)}
                  </Avatar>
                  <Box sx={{
                    position: 'absolute', bottom: 3, right: 3,
                    width: 13, height: 13, borderRadius: '50%',
                    bgcolor: colors.success.primary,
                    border: `2px solid ${colors.background.default}`,
                    boxShadow: `0 0 6px ${colors.success.primary}`,
                  }} />
                </Box>
                <Box>
                  <Typography variant="h5" fontWeight={700} sx={{
                    letterSpacing: '-0.02em', lineHeight: 1.2,
                    background: `linear-gradient(90deg, ${colors.text.primary} 60%, ${colors.blue.light})`,
                    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                  }}>
                    Welcome back, {(userData as any)?.displayName || user?.email?.split('@')[0]}
                  </Typography>
                  <Typography variant="body2" sx={{ color: alpha(colors.text.secondary, 0.7), mt: 0.4, letterSpacing: '0.01em' }}>
                    Ready to dominate the courts?
                  </Typography>
                </Box>
              </Box>

              {/* Right: action buttons */}
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<GroupAdd sx={{ fontSize: '1rem' }} />}
                  onClick={() => navigate('/leagues/join')}
                  sx={{
                    borderRadius: 2.5, px: 2.5, py: 1,
                    fontWeight: 600, fontSize: '0.8rem', letterSpacing: '0.02em',
                    background: `linear-gradient(135deg, ${colors.orange.primary}, ${colors.orange.dark})`,
                    boxShadow: `0 4px 14px ${alpha(colors.orange.primary, 0.4)}`,
                    '&:hover': {
                      background: `linear-gradient(135deg, ${colors.orange.light}, ${colors.orange.primary})`,
                      boxShadow: `0 6px 18px ${alpha(colors.orange.primary, 0.55)}`,
                      transform: 'translateY(-1px)',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  Join League
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Add sx={{ fontSize: '1rem' }} />}
                  onClick={() => navigate('/leagues/create')}
                  sx={{
                    borderRadius: 2.5, px: 2.5, py: 1,
                    fontWeight: 600, fontSize: '0.8rem', letterSpacing: '0.02em',
                    borderColor: alpha(colors.blue.electric, 0.5), color: colors.blue.light,
                    backdropFilter: 'blur(8px)',
                    '&:hover': {
                      borderColor: colors.blue.electric,
                      bgcolor: alpha(colors.blue.electric, 0.1),
                      transform: 'translateY(-1px)',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  Create League
                </Button>
                <Button
                  variant="text"
                  size="small"
                  startIcon={<Settings sx={{ fontSize: '1rem' }} />}
                  onClick={() => navigate('/profile')}
                  sx={{
                    borderRadius: 2.5, px: 2, py: 1,
                    fontWeight: 500, fontSize: '0.8rem',
                    color: alpha(colors.text.secondary, 0.6),
                    '&:hover': { color: colors.text.secondary, bgcolor: alpha(colors.grey[600], 0.12) },
                    transition: 'all 0.2s ease',
                  }}
                >
                  Profile
                </Button>
              </Box>
            </Box>

            {/* Divider with label */}
            <Box sx={{ px: { xs: 3, md: 5 }, mb: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ flex: 1, height: '1px', background: `linear-gradient(90deg, ${alpha(colors.blue.electric, 0.2)}, transparent)` }} />
                <Typography variant="caption" sx={{
                  color: alpha(colors.text.secondary, 0.8),
                  letterSpacing: '0.12em', textTransform: 'uppercase',
                  fontWeight: 600, fontSize: '0.68rem',
                }}>
                  Your Tennis Leagues&nbsp;·&nbsp;{loading ? '...' : tennisLeagues.length}
                </Typography>
                <Box sx={{ flex: 1, height: '1px', background: `linear-gradient(90deg, transparent, ${alpha(colors.blue.electric, 0.2)})` }} />
              </Box>
            </Box>

            {/* League mini-cards */}
            <Box sx={{ px: { xs: 3, md: 5 }, pb: { xs: 3, md: 4 } }}>
              {loading ? (
                <Box display="flex" justifyContent="center" py={3}>
                  <CircularProgress size={28} sx={{ color: colors.blue.electric }} />
                </Box>
              ) : tennisLeagues.length > 0 ? (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2,1fr)', md: 'repeat(3,1fr)' }, gap: 2 }}>
                  {tennisLeagues.slice(0, 3).map(league => {
                    const { color, label, pulse } = getLeagueStatus(league);
                    return (
                      <Card
                        key={league.id}
                        onClick={() => navigate(`/leagues/${league.id}`)}
                        sx={{
                          cursor: 'pointer',
                          background: `linear-gradient(135deg, ${alpha(colors.blue.deep, 0.5)} 0%, ${alpha(colors.blue.navy, 0.3)} 100%)`,
                          backdropFilter: 'blur(12px)',
                          border: `1px solid ${alpha(colors.blue.electric, 0.12)}`,
                          borderRadius: 3,
                          transition: 'all 0.25s cubic-bezier(0.4,0,0.2,1)',
                          '&:hover': {
                            transform: 'translateY(-3px)',
                            border: `1px solid ${alpha(colors.blue.electric, 0.4)}`,
                            boxShadow: `0 12px 28px rgba(0,0,0,0.3)`,
                            background: `linear-gradient(135deg, ${alpha(colors.blue.deep, 0.7)} 0%, ${alpha(colors.blue.navy, 0.5)} 100%)`,
                          },
                        }}
                      >
                        <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                          <Typography variant="body2" fontWeight={700} noWrap sx={{ letterSpacing: '0.02em', color: colors.text.primary, mb: 0.5 }}>
                            {league.name}
                          </Typography>
                          <Typography variant="caption" sx={{ color: alpha(colors.text.secondary, 0.6), display: 'block', mb: 1.5 }}>
                            {league.tournamentName}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            {/* Tennis badge */}
                            <Box sx={{ px: 1, py: 0.25, borderRadius: 1, border: `1px solid ${alpha(colors.blue.electric, 0.4)}`, bgcolor: alpha(colors.blue.electric, 0.08) }}>
                              <Typography variant="caption" sx={{ color: colors.blue.light, fontWeight: 700, fontSize: '0.65rem', letterSpacing: '0.06em' }}>
                                TENNIS
                              </Typography>
                            </Box>
                            {/* Status badge */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.25, borderRadius: 1, border: `1px solid ${alpha(color, 0.4)}`, bgcolor: alpha(color, 0.08) }}>
                              <Box sx={{
                                width: 5, height: 5, borderRadius: '50%', bgcolor: color,
                                ...(pulse && {
                                  boxShadow: `0 0 5px ${color}`,
                                  animation: 'pulse 2s infinite',
                                  '@keyframes pulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.4 } },
                                }),
                              }} />
                              <Typography variant="caption" sx={{ color, fontWeight: 600, fontSize: '0.65rem', letterSpacing: '0.04em' }}>
                                {label}
                              </Typography>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    );
                  })}
                </Box>
              ) : (
                <Box sx={{ py: 3, textAlign: 'center', border: `1px dashed ${alpha(colors.blue.electric, 0.2)}`, borderRadius: 3 }}>
                  <SportsTennis sx={{ fontSize: 24, color: alpha(colors.text.primary, 0.2), mb: 1 }} />
                  <Typography variant="body2" sx={{ color: alpha(colors.text.secondary, 0.5) }}>
                    No tennis leagues yet. Create or join one to get started.
                  </Typography>
                </Box>
              )}
            </Box>

          </Card>
        </Fade>
      </Container>

      {/* ── Content grid ─────────────────────────────────────────────────── */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 5 } }}>
        <Grid container spacing={3}>

          {/* My Tennis Leagues - full list */}
          <Grid size={{ xs: 12, md: 7 }}>
            <Box sx={{
              position: 'relative', pl: 2, mb: 2.5,
              '&::before': {
                content: '""', position: 'absolute', left: 0, top: 0, bottom: 0,
                width: '3px', borderRadius: '3px',
                background: `linear-gradient(180deg, ${colors.blue.electric}, ${alpha(colors.blue.electric, 0.2)})`,
              },
            }}>
              <Typography sx={{
                fontFamily: "'Montserrat', sans-serif", fontWeight: 800,
                fontSize: { xs: '1rem', md: '1.15rem' },
                background: colors.gradients.title,
                backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
              }}>
                My Tennis Leagues
              </Typography>
            </Box>

            <Card sx={glassCard}>
              <CardContent sx={{ p: 0 }}>
                {loading ? (
                  <Box display="flex" justifyContent="center" py={5}>
                    <CircularProgress size={32} sx={{ color: colors.blue.electric }} />
                  </Box>
                ) : tennisLeagues.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 6, px: 3 }}>
                    <SportsTennis sx={{ fontSize: 32, color: alpha(colors.text.primary, 0.18), mb: 1.5 }} />
                    <Typography sx={{ fontFamily: "'Satoshi', sans-serif", fontWeight: 600, color: alpha(colors.text.primary, 0.5), mb: 0.5 }}>
                      No tennis leagues yet
                    </Typography>
                    <Typography sx={{ fontFamily: "'Satoshi', sans-serif", fontSize: '0.8rem', color: alpha(colors.text.primary, 0.28) }}>
                      Create one to get started
                    </Typography>
                  </Box>
                ) : (
                  tennisLeagues.map((league, idx) => {
                    const { color, label, pulse } = getLeagueStatus(league);
                    const isLast = idx === tennisLeagues.length - 1;
                    return (
                      <Box
                        key={league.id}
                        onClick={() => navigate(`/leagues/${league.id}`)}
                        sx={{
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          px: 2.5, py: 2, cursor: 'pointer',
                          borderBottom: isLast ? 'none' : `1px solid rgba(255,255,255,0.06)`,
                          transition: 'background 0.15s ease',
                          '&:hover': { bgcolor: alpha(colors.blue.electric, 0.06) },
                        }}
                      >
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography sx={{
                            fontFamily: "'Satoshi', sans-serif", fontWeight: 700,
                            fontSize: '0.9rem', color: colors.text.primary,
                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                          }}>
                            {league.name}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.4 }}>
                            <Typography sx={{ fontFamily: "'Satoshi', sans-serif", fontSize: '0.72rem', color: alpha(colors.text.secondary, 0.55) }}>
                              {league.tournamentName}
                            </Typography>
                            <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: alpha(colors.text.secondary, 0.3) }} />
                            <People sx={{ fontSize: 11, color: alpha(colors.text.secondary, 0.45) }} />
                            <Typography sx={{ fontFamily: "'Satoshi', sans-serif", fontSize: '0.72rem', color: alpha(colors.text.secondary, 0.45) }}>
                              {league.participants.length}
                            </Typography>
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0, ml: 2 }}>
                          <Box sx={{
                            display: 'flex', alignItems: 'center', gap: 0.6,
                            px: 1.25, py: 0.4, borderRadius: 1.5,
                            border: `1px solid ${alpha(color, 0.4)}`, bgcolor: alpha(color, 0.08),
                          }}>
                            <Box sx={{
                              width: 6, height: 6, borderRadius: '50%', bgcolor: color,
                              ...(pulse && {
                                boxShadow: `0 0 5px ${color}`,
                                animation: 'rowPulse 2s infinite',
                                '@keyframes rowPulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.4 } },
                              }),
                            }} />
                            <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color, letterSpacing: '0.04em', fontFamily: "'Satoshi', sans-serif" }}>
                              {label}
                            </Typography>
                          </Box>
                          <ArrowForward sx={{ fontSize: 14, color: alpha(colors.blue.electric, 0.45) }} />
                        </Box>
                      </Box>
                    );
                  })
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* How Scoring Works */}
          <Grid size={{ xs: 12, md: 5 }}>
            <Box sx={{
              position: 'relative', pl: 2, mb: 2.5,
              '&::before': {
                content: '""', position: 'absolute', left: 0, top: 0, bottom: 0,
                width: '3px', borderRadius: '3px',
                background: `linear-gradient(180deg, ${colors.blue.electric}, ${alpha(colors.blue.electric, 0.2)})`,
              },
            }}>
              <Typography sx={{
                fontFamily: "'Montserrat', sans-serif", fontWeight: 800,
                fontSize: { xs: '1rem', md: '1.15rem' },
                background: colors.gradients.title,
                backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
              }}>
                How Scoring Works
              </Typography>
            </Box>

            <Card sx={glassCard}>
              <CardContent sx={{ p: 2.5 }}>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <EmojiEvents sx={{ fontSize: 14, color: colors.blue.electric }} />
                  <Typography sx={{
                    fontFamily: "'Satoshi', sans-serif", fontWeight: 700, fontSize: '0.78rem',
                    color: colors.blue.electric, textTransform: 'uppercase', letterSpacing: '0.06em',
                  }}>
                    Slot Multipliers
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mb: 3 }}>
                  {slotMultipliers.map(({ slot, mult, fill }) => (
                    <Box key={slot} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Typography sx={{
                        fontFamily: "'Satoshi', sans-serif", fontSize: '0.72rem', fontWeight: 600,
                        color: alpha(colors.text.secondary, 0.7), width: 54, flexShrink: 0,
                      }}>
                        {slot}
                      </Typography>
                      <Box sx={{ flex: 1, height: 5, borderRadius: '3px', bgcolor: alpha(colors.blue.navy, 0.8), border: `1px solid ${colors.border.subtle}`, overflow: 'hidden' }}>
                        <Box sx={{
                          width: `${fill}%`, height: '100%', borderRadius: '3px',
                          background: `linear-gradient(90deg, ${colors.blue.electric}, ${colors.blue.light})`,
                        }} />
                      </Box>
                      <Typography sx={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '0.85rem', color: colors.blue.light, width: 40, textAlign: 'right', flexShrink: 0 }}>
                        {mult}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                <Box sx={{ height: '1px', bgcolor: 'rgba(255,255,255,0.07)', mb: 2.5 }} />

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <Dashboard sx={{ fontSize: 14, color: colors.blue.electric }} />
                  <Typography sx={{
                    fontFamily: "'Satoshi', sans-serif", fontWeight: 700, fontSize: '0.78rem',
                    color: colors.blue.electric, textTransform: 'uppercase', letterSpacing: '0.06em',
                  }}>
                    Point Events
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6, mb: 2.5 }}>
                  {pointEvents.map(({ label, pts }) => (
                    <Box key={label} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                        <FiberManualRecord sx={{ fontSize: 5, color: alpha(colors.text.secondary, 0.35), flexShrink: 0 }} />
                        <Typography sx={{
                          fontFamily: "'Satoshi', sans-serif", fontSize: '0.78rem',
                          color: alpha(colors.text.secondary, 0.7),
                          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                        }}>
                          {label}
                        </Typography>
                      </Box>
                      <Typography sx={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '0.82rem', color: colors.blue.light, flexShrink: 0 }}>
                        {pts}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                <Box sx={{ p: 1.5, borderLeft: `3px solid ${colors.blue.electric}`, bgcolor: alpha(colors.blue.electric, 0.07), borderRadius: '0 8px 8px 0' }}>
                  <Typography sx={{ fontSize: '0.75rem', fontFamily: "'Satoshi', sans-serif", color: alpha(colors.text.primary, 0.65), lineHeight: 1.55 }}>
                    Pick 5 men's and 5 women's players. Rank them M1 to M5 and W1 to W5. Higher slots earn bigger multipliers.
                  </Typography>
                </Box>

              </CardContent>
            </Card>
          </Grid>

        </Grid>
      </Container>
    </Box>
  );
};

export default TennisHubPage;
